import { NodeManager } from "./NodeManager";
import { ArrowManager } from "./ArrowManager";
import { flattenTree, getSrcAndDstFromEvents, getTransactionChildrenInOrder } from "../../../utils/commonUtils";
import { calculatePaperHeight, calculatePaperWidth, findAllParents, getGapBetweenNodes, moveChildTransactionBackgroundToBack, removeArrowGroups, renderBackgroundWrapperForPaper, renderDiagonalStripePatternForChildTransction, renderStyledTooltip } from "../../../utils/raphaelHelpers";
import { getTransactionClusterlets, getTransactionElementsCount } from "../../../utils/transactionUtils";
import { handleSnackBar } from "../../../store/global/actions";
const { Raphael } = require('react-raphael');

export class DiagramManager {
  constructor(container, { dispatch, selectedTransaction, clusterlets, events, messages, fields, transactions,  connections, transactionEvents,   handleSelectedItem, handleDeleteItem, setQuickViewMessages, onElementRename, removeExistingInlineInput, checkCodeEditorStatus, utilityFunctions, setSelectedUtilityFunction}) {
    this.dispatch = dispatch;
    this.containerRef = container;
    this.containerHeight = window.innerHeight - 70;
    
    this.defaultGapBetweenNodes = 300;
    this.nodeVerticalLineOffset = 100;
    this.singleElementHeight = 60;
    this.createElementIconHeight = 80;
    this.createElementIconOffset = 100;
    this.edgePadding = 100;
    this.gapBetweenNodes = getGapBetweenNodes(container.clientWidth , this.defaultGapBetweenNodes);

    this.transactionChildrenInOrder = getTransactionChildrenInOrder({transaction: selectedTransaction, allTransactions: transactions, allEvents: events});
    this.transactionElementsCount = getTransactionElementsCount({transactionElements: this.transactionChildrenInOrder});
    this.firstLevelChildTransactions = this.transactionChildrenInOrder?.filter(item => item?.type === "nested")?.map(child => child?.id) || [];

    this.paperWidth = calculatePaperWidth({
      nodeCount: selectedTransaction?.clusterlets?.length,
      gapBetweenNodes: this.gapBetweenNodes,
      containerWidth: container.clientWidth,
      edgePadding: this.edgePadding,
    })
    this.paperHeight = calculatePaperHeight({
      containerHeight: this.containerHeight, 
      verticalElementsCount: this.transactionElementsCount,
      singleElementHeight: this.singleElementHeight,
      nodeVerticalLineOffset: this.nodeVerticalLineOffset,
      createElementIconHeight: this.createElementIconHeight,
      createElementIconOffset: this.createElementIconOffset
    })
    this.paper = Raphael(container, this.paperWidth, this.paperHeight);
    this.defaultNodeVerticalLineHeight = this.containerHeight;

    const canvasStyle = this.paper.canvas.style;
    canvasStyle.backgroundColor = 'rgb(237 237 237)';
    canvasStyle.backgroundImage = `radial-gradient(circle, rgba(136, 136, 136, 0.3) 1px, transparent 0)`;
    canvasStyle.backgroundSize = '15px 15px';
    canvasStyle.backgroundRepeat = 'repeat';
    canvasStyle.overflow = 'auto';
    this.paper.canvas.id = 'dataflow-canvas-svg';

    this.nodeManager = new NodeManager({
      diagramManager: this, 
      paper: this.paper, 
      dispatch: this.dispatch, 
      selectedTransaction, 
      events, 
      clusterlets, 
      transactions, 
      handleSelectedItem,
      defaultNodeVerticalLineHeight: this.defaultNodeVerticalLineHeight,
      checkCodeEditorStatus
    } );

    
    this.arrowManager = new ArrowManager({
      diagramManager: this, 
      paper: this.paper, 
      nodeManager: this.nodeManager, 
      dispatch: this.dispatch, 
      connections: connections, 
      events, 
      clusterlets, 
      transactions, 
      utilityFunctions: utilityFunctions,
      selectedTransaction, 
      handleSelectedItem, handleDeleteItem,
      firstLevelChildTransactions: this.firstLevelChildTransactions,
      messages,
      setQuickViewMessages,
      onElementRename,
      removeExistingInlineInput,
      checkCodeEditorStatus,
      setSelectedUtilityFunction
    });
    this.nodeManager.setArrowManager(this.arrowManager);
  
    this.checkCodeEditorStatus = checkCodeEditorStatus;
    this.initailRendering=true;
    this.selectedTransaction = selectedTransaction;
    this.connections = connections;
    this.clusterlets = clusterlets;
    this.events = events;
    this.transactionEvents = transactionEvents;
    this.transactions = transactions;
    this.preventCompleteRerender = false;
    this.preventConstructsUpdate = false;
    this.selectedItem = null;
    this.focusElement = null;
    this.hoveringItem = null;
    this.maxLines = 1;


    this.styledTooltip = renderStyledTooltip();
    this.setEventLabelBoxHeight();
  }

  
  hoveringElementConfig = {
    mouseOverFill: "#fcfc69"
  }

  setEventLabelBoxHeight(){
    let names = [];

    const pushChildren = (children) => {
      children.forEach(child => {
        if(child.children){
          names.push(this.transactions.find(txn => txn._id === child.id)?.name);
          pushChildren(child.children)
        }else{
          names.push(this.events.find(e => e._id === child.id)?.name);
        }
      })
    }

    pushChildren(this.transactionChildrenInOrder)    
    names = names.filter(name => Boolean(name)).map(name => this.arrowManager.splitLabel(name))

    let maxLines = 1;

    names.forEach(name => {
      maxLines = Math.max(maxLines, name.length)
    })

    if(maxLines > 1 && this.maxLines < maxLines){
      this.arrowManager.labelBoxConfig.height = ((maxLines * this.arrowManager.labelBoxConfig.fontSize * 1.2) + 8)
      this.nodeManager.plusShiftStep += (maxLines - 1) * 2
      this.maxLines = maxLines
    }

  }

  scrollToIfNeeded(newY) {
    const padding = 100;
    const container = this.container;
    if (!container) return;

    const containerHeight = this.containerHeight;
    const scrollTop = container.scrollTop;
    const visibleBottom = scrollTop + containerHeight;

    if (newY + padding > visibleBottom) {
      const scrollAmount = newY - containerHeight + padding;
      container.scrollTo({ top: scrollAmount, behavior: 'smooth' });
    }
  }
  /**
   * Initializes and renders the complete transaction diagram.
   *
   * Responsibilities:
   * - Calculates and sets the height of the drawing canvas based on expanded child transactions.
   * - Renders the background, stripe pattern, nodes, events, and child transactions.
   * - Handles scroll positioning and DOM layering of elements.
   * - Enables interactive behaviors such as drag-and-drop and hover effects.
   * - Applies pop-up icons and visual indicators like highlights.
   * - Prepares the diagram for user interactions and resets internal tracking states.
   */
  init() {

    // Update the height of the diagram on expand/collapse of the child transactions
    const expandedItems = this.arrowManager?.expandedChildTransactions;
    this.transactionElementsCount = getTransactionElementsCount({transactionElements: this.transactionChildrenInOrder, expandItems: expandedItems});
    this.resetPaperHeight();

    this.paperBackgroundWrapper = renderBackgroundWrapperForPaper({paper: this.paper, width: this.paperWidth, height: this.paperHeight});

    renderDiagonalStripePatternForChildTransction({paper: this.paper})
    
    this.renderAllNodes();

    this.renderAllEventsAndChildTxns({item: this.selectedTransaction, children: this.transactionChildrenInOrder});

    this.scrollToElementIfNeeded(this.focusElement);
    this.arrowManager.addInputToElement();
    
    moveChildTransactionBackgroundToBack({childTxnElements: Object.values(this.arrowManager?.childTransactions)})

    this.nodeManager.renderPopUpIcons();
    
    // Apply drag and drop for clusterlets  
    this.nodeManager.dragClusterletToReorder();

    this.arrowManager.dragEventAndTransactionsToReorder();

    this.nodeManager.onHoverVerticalLine();

    this.bindMouseOverEventsToBackground();

    // To highlight item in the diagram, if there is selected item already
    if(this.selectedItem) this.hightLighSelectedElementInDiagram(this.selectedItem);

    this.arrowManager.lastExpandedChild = null;
    this.focusElement = null;
    this.initialRendering = false;
    this.nodeManager.disableExpandButtonClick = false;

  }

  

  bindMouseOverEventsToBackground() {
    if(!this.paperBackgroundWrapper) return;

    this.paperBackgroundWrapper.mouseover(() => {
      this.nodeManager?.hidePopUpSet();
      this.removeHoveringHighlight();
    })
  }


  

  updateTransactionData(data){
    const {selectedTransaction, clusterlets, events, transactionEvents,  transactions, connections, messages} = data;

    if(selectedTransaction) this.selectedTransaction = selectedTransaction;
    if(transactions) this.transactions = transactions;
    if(connections) this.connections = connections;
    if(clusterlets) this.clusterlets = clusterlets;
    if(events) this.events = events;
    if(transactionEvents) this.transactionEvents = transactionEvents;
    if(messages) this.messages = messages
    
    this.transactionChildrenInOrder = getTransactionChildrenInOrder({transaction: this.selectedTransaction, allTransactions: this.transactions, allEvents: this.events})
      
    this.arrowManager?.updateTransactionData(data);
    this.nodeManager?.updateTransactionData(data);
    // update paper width
    this.updatePaperWidth({paper: this.paper, nodesCount: this.selectedTransaction?.clusterlets?.length} );
    
    // update paper height
    this.transactionElementsCount = getTransactionElementsCount({transactionElements: this.transactionChildrenInOrder, expandItems: this.arrowManager?.expandedChildTransactions });
    this.resetPaperHeight();
    this.setEventLabelBoxHeight();


  }
  

  //  To render all nodes in the transaction
  renderAllNodes(){
    const startX = 100;
    const startY = 100;
    const xSpacing = this.gapBetweenNodes || this.defaultGapBetweenNodes;

    const transactionNodes = getTransactionClusterlets({transaction: this.selectedTransaction, clusterlets: this.clusterlets})

    transactionNodes?.forEach((node, index) => {
      const x = startX + index * xSpacing;
      const y = startY;
      this.nodeManager.renderNode(node.id, x, y, node.label, node.icon, node.type, node);
    });
  }

  //  To render all the events and childrenrender
  renderAllEventsAndChildTxns({item, children, firstLevelChild=true}){
    let clusterletsToConsider = this.selectedTransaction?.clusterlets.sort((a, b) => a.order - b.order) || [];
    const lastExpandedChild = this.arrowManager?.lastExpandedChild;
    const expandedList = this.arrowManager?.expandedChildTransactions;

    children?.forEach((child, index) => {
      if (child.type === 'event') {
        const event = this.events?.find((e) => e._id === child.id);
        if(event === undefined || event === null) return;
        
        this.arrowManager.renderEvent({eventId: event._id, fromId: event.src, toId: event.dst, label: event.name, onsend: event?.attributes?.onsend, onrecv: event?.attributes?.onrecv, parentId:  child?.parent});
        this.nodeManager.incrementLevelAndUpdatePlusIcons();
      }else if (child.type === 'nested') {
        let childTransaction = this.transactions.find(tr => tr._id === child.id);
        if(childTransaction === undefined || childTransaction === null) return;

        
        const isExpanded = expandedList && expandedList[child?.id]  ? true : false;
        
        let {src, dst } = getSrcAndDstFromEvents({ transaction: this.selectedTransaction, child, allEvents: this.events, allTransactions : this.transactions, selectedTx : this.selectedTransaction });
        
        const isParentNestedTxn = this.transactions?.find(tr => tr._id === child?.parent)?.type === 9 ? true : false;
        const parentTransactionElement = isParentNestedTxn ? Object.values(this.arrowManager?.childTransactions)?.find(item => item?.id === child?.parent) : null;
        if(isParentNestedTxn && parentTransactionElement && child?.children?.length === 0){
          src = parentTransactionElement?.source;
          dst = parentTransactionElement?.target;
        }

        let childrenCount = this.getElementsCount(child?.children);

        if(isExpanded){
          this.arrowManager.renderTransaction({
            fromId: src,
            toId: dst,
            label: childTransaction ? childTransaction.name : "name",
            transactionId: childTransaction._id,
            parentId: child?.parent,
            firstLevelChild,
            childrenCount,
            children: child?.children,
            isExpanded: true,
          });
          this.nodeManager.incrementLevelAndUpdatePlusIcons();
          if(child?.children && child?.children?.length > 0){
            this.renderAllEventsAndChildTxns({item: childTransaction, children: child?.children, firstLevelChild: false});
            this.nodeManager.incrementLevelAndUpdatePlusIcons();
          }else{
            this.nodeManager.incrementLevelAndUpdatePlusIcons();
            this.nodeManager.incrementLevelAndUpdatePlusIcons();
          }

          const eventElements =  Object.values(this.arrowManager?.eventArrows) || [];
          const childTxnElements = Object.values(this.arrowManager?.childTransactions) || [];
          const invokedTxnElements = Object.values(this.arrowManager?.invokedTransactions) || [];
          this.arrowManager?.renderBackgroundForChildTransaction({transactionId: child?.id, children: child?.children, expandedList, lastExpandedChild, eventElements, childTxnElements, invokedTxnElements, firstLevelChild} );
          this.nodeManager.incrementLevelAndUpdatePlusIcons();
        }else{
          this.arrowManager.renderTransaction({
            fromId: src,
            toId: dst,
            label: childTransaction ? childTransaction.name : "name",
            transactionId: childTransaction._id,
            parentId: child?.parent,
            firstLevelChild,
            childrenCount,
            isExpanded:false,
          });
          this.nodeManager.incrementLevelAndUpdatePlusIcons();
        }
      } else if (child.type === 'invoked') {
        let invokedTransaction = this.transactions.find(tr => tr._id === child.id);
        if(invokedTransaction === null || invokedTransaction === undefined) return;

        const commonClusterlets = this.hasAtLeastTwoCommonIds(this.selectedTransaction?.clusterlets, invokedTransaction?.clusterlets)

        let src  = commonClusterlets[0]?.id ? commonClusterlets[0]?.id : clusterletsToConsider[0]?.id;
        let dst = commonClusterlets[commonClusterlets?.length - 1]?.id ? commonClusterlets[commonClusterlets.length - 1]?.id : clusterletsToConsider[clusterletsToConsider.length - 1].id;

        this.arrowManager.renderInvokedTransaction({
          fromId: src,
          toId: dst,
          label: invokedTransaction ? invokedTransaction.name : "name",
          transactionId: invokedTransaction._id,
          parentId: child.parent ,
          uuid: child?.uuid
        });
        this.nodeManager.incrementLevelAndUpdatePlusIcons();
      }
    })
  }

  hasAtLeastTwoCommonIds(selected, invoked) {
    const importedIds = new Set(invoked.map(item => item.id));
    return selected.filter(item => importedIds.has(item.id)).sort((a, b) => a.order - b.order);
  }
  

  getElementsCount(elements){

    let eventsCount = 0;
    let nestedTxCount = 0;
    let invokedTxCount = 0;
    if(elements === undefined ){
      return {eventsCount, nestedTxCount, invokedTxCount}

    }
    elements.forEach(el => {
      if(el.type === "event") eventsCount++;
      if(el.type === "nested") nestedTxCount++;
      if(el.type === "invoked") invokedTxCount++;
    })

    return {eventsCount, nestedTxCount, invokedTxCount}

  }



  getAllPaperElements(){
    return {
      events: this.arrowManager?.eventArrows,
      childTransactions: this.arrowManager?.childTransactions,
      invokedTransactions: this.arrowManager?.invokedTransactions,
      nodes: this.nodeManager?.nodes,
    }
  }

  // To update the width of the paper
  updatePaperWidth({paper, nodesCount}){
    const newWidth =  calculatePaperWidth({
      nodeCount: nodesCount,
      gapBetweenNodes: this.gapBetweenNodes,
      containerWidth: this.containerRef?.clientWidth,
      edgePadding: this.edgePadding
    })
    paper.setSize(newWidth, this.paperHeight);
  }

  // To update the height of a Raphael paper with respect to its current height:
  resetPaperHeight() {

    const paper = this.paper;
    const elementsCount = this.transactionElementsCount;

    const newHeight = calculatePaperHeight({
      containerHeight: this.containerRef.clientHeight, 
      verticalElementsCount: elementsCount,
      singleElementHeight: this.singleElementHeight,
      nodeVerticalLineOffset: this.nodeVerticalLineOffset,
      createElementIconHeight: this.createElementIconHeight,
      createElementIconOffset: this.createElementIconOffset
    })

    this.paperHeight = newHeight;

    paper.setSize(paper.width, newHeight);
  }

  removeHighlightingInDiagram = () => {
    if(this.selectedItem){
      this.selectedItem?.highlightElement?.attr({ fill: "#fff" });
    }
    this.selectedItem  = null;
  }

  hightLighSelectedElementInDiagram = ({id, type }) => {
    let codeEditStatus = this.checkCodeEditorStatus() !== undefined ? this.checkCodeEditorStatus() : true;
    if(codeEditStatus === true){
      return;
    }

    this.removeHighlightingInDiagram();
    this.removeHoveringHighlight();

    
    const events = Object.values(this.arrowManager?.eventArrows) || [];
    const invoked_transactions = Object.values(this.arrowManager?.invokedTransactions) || [];
    const child_transactions = Object.values(this.arrowManager?.childTransactions) || [];
    const nodes =  Object.values(this.nodeManager?.nodes) || [];

    let selectedItem = null;
    if(type === "event"){
      selectedItem = events?.find( item  => item?.id === id) ;
    }else if (type === "invoked_transaction"){
      selectedItem = invoked_transactions?.find( item  => item?.id === id) ;
    }else if (type === "nested_transaction"){
      selectedItem = child_transactions?.find( item  => item?.id === id) ;
    }else if (type === "clusterlet" || type === "analytics" || type === "external_function"){
      selectedItem = nodes?.find( item  => item?.id === id) ;
    }

    if(selectedItem){
      selectedItem?.highlightElement?.attr({ fill: 'yellow' });

      this.selectedItem = {id, type, highlightElement: selectedItem?.highlightElement};
    }

  }


  handleInvokeTransactionCreation({id, parent, invokedTxnId, uuid}){
    let data = {}
    const child = this.transactions?.find(item => item?._id === id);
    if(child === undefined){
      return;
    }

    const parentTxn = this.transactions?.find(item => item?._id === parent);
    const order = parentTxn?.nestedTransactions?.find(txn => txn?.id === id)?.order;
    const newInvokedItem = {
      id: invokedTxnId,
      uuid,
      order
    }

    const updatedTrasactions = this.transactions?.filter(item => item?._id !== id)?.map(txn => {
      if(txn?._id === parent){
        txn.nestedTransactions = txn.nestedTransactions?.filter(txn => txn?.id !== id) || []; 
        txn.invokedTransactions = [...txn.invokedTransactions, newInvokedItem] || []; 
      }

      return txn;
    })


    if(this.selectedTransaction?._id === parent ){
      this.selectedTransaction.nestedTransactions = this.selectedTransaction?.nestedTransactions?.filter(txn => txn?.id !== id); 
      this.selectedTransaction.invokedTransactions = [...this.selectedTransaction?.invokedTransactions, newInvokedItem];
    }

    data.transactions = updatedTrasactions;
    data.selectedTransaction = this.selectedTransaction;

    this.arrowManager.lastExpandedChild = null;
    this.preventCompleteRerender = true;
    this.updateTransactionData(data);
    this.clearAllElements();
    this.init();
  }

  async removeElementsFromDiagram({id, type}){
    let data = {};
    const eventElements = Object.values(this.arrowManager?.eventArrows) || [] ;
    const childTxnElements = Object.values(this.arrowManager?.childTransactions) || [];
    const invokedTxnElements = Object.values(this.arrowManager?.invokedTransactions) || [];

    
    if(type === "event"){
      const eventElement = eventElements?.find(item => item?.id === id)
      const parentId = eventElement?.parentId;
      const updatedEvents = this.events?.filter(event => event?._id !== id);
      const parentTxn = this.transactions?.find(txn => txn?._id === parentId);
      
      const updatedTransctions = this.transactions?.map(txn => {
        if(txn?._id === parentId){
          const updatedEventsList = [...txn?.events?.filter(ev => ev?.id !== id) ];
          txn.events = updatedEventsList;
        }
        return txn;
      })

      if(parentTxn?._id === this.selectedTransaction?._id){
        const updatedEventsList = [...this.selectedTransaction?.events?.filter(ev => ev?.id !== id) ];
        this.selectedTransaction = {
          ...this.selectedTransaction,
          events: updatedEventsList
        }

        await removeArrowGroups([eventElement?.group]);
      }

      data.events = updatedEvents;
      data.transactions = updatedTransctions;
      data.selectedTransaction = this.selectedTransaction;
    }else if(type === "nested_transaction"){
      const parentId  = childTxnElements?.find(item => item?.id === id)?.parentId;
      const updatedTransactions = this.transactions?.filter(txn => txn?._id !== id)?.map(txn => {
        if(txn?._id === parentId){
          txn.nestedTransactions = txn.nestedTransactions?.filter(item => item?.id !== id);
        }
        return txn;
      });
      
      if(this.selectedTransaction?._id === parentId){
        this.selectedTransaction = {
          ...this.selectedTransaction,
          nestedTransactions: [...this.selectedTransaction.nestedTransactions?.filter(item => item?.id !== id) ]
        }
      }

      data.transactions = updatedTransactions;
      data.selectedTransaction = this.selectedTransaction;

      await this.arrowManager?.handleChildtransactionCollapse(id);
    }else if(type === "invoked_transaction"){
      const invokedElement = invokedTxnElements?.find(item => item?.id === id);
      const parentId  = invokedElement?.parentId;
      const updatedTransactions = this.transactions?.filter(txn => txn?._id !== id)?.map(txn => {
        if(txn?._id === parentId){
          txn.invokedTransactions = txn.invokedTransactions?.filter(item => item?.uuid !== id);
        }
        return txn;
      });

      if(this.selectedTransaction?._id === parentId){
        this.selectedTransaction = {
          ...this.selectedTransaction,
          invokedTransactions: [...this.selectedTransaction.invokedTransactions?.filter(item => item?.uuid !== id) ]
        }
      }

      data.transactions = updatedTransactions;
      data.selectedTransaction = this.selectedTransaction;

      await removeArrowGroups([invokedElement?.group]);
    }

    this.arrowManager.lastExpandedChild = null;
    this.preventCompleteRerender = true;
    this.updateTransactionData(data);
    this.clearAllElements();
    this.init();
  }

  handleElementUpdate({id, type, data}){
    let updatedData = {};


    if(type === "event"){
      const newSource = this.clusterlets?.find(item => item?._id === data?.src);
      const newTarget = this.clusterlets?.find(item => item?._id === data?.dst);
      
      let onsend = newSource?.type === 1 ? false : data?.attributes?.onsend;
      let onrecv = newTarget?.type === 1 ? false : data?.attributes?.onrecv;
      const updatedEvents = this.events?.map(item => {
        if(item?._id === id){
          item = {
            ...item,
            name : data?.name,
            desc : data?.desc || "",
            src: data?.src,
            dst : data?.dst,
            message : data.message,
            attributes: {
              onsend,
              onrecv
            }
          }
        }
        return item;
      });
      updatedData.events = updatedEvents;

    }else if(type === "child_transaction"){

    }else if(type === "clusterlet"){

    }

    this.arrowManager.lastExpandedChild = null;
    this.diagramManager?.updateTransactionData(updatedData);
    this.diagramManager?.clearAllElements();
    this.diagramManager?.init();
  }

  
  clearAllElements(){
    this.paper.clear();
    
    this.nodeManager.level = 0;
    this.nodeManager.nodes = {};
    this.nodeManager.newNodePositions = {};
    this.nodesForPlusCircle = {};

    this.nodeManager.popupSetItems = {};
    this.nodeManager.popupSetElements = {};
    this.nodeManager.popupBufferRect = {};
    this.nodeManager.globalPopUpSet = null;

    this.arrowManager.eventArrows = {};
    this.arrowManager.invokedTransactions = {};
    this.arrowManager.childTransactions = {};
    this.arrowManager.draggableItems = [];
    this.arrowManager.drawnInvokedTransactions = [];
    this.arrowManager.nodesForPlusCircle ={};
    this.arrowManager.transactionPopups = {};
    this.arrowManager.drawnEvents = [];
    this.arrowManager.drawnChildTransactions = [];
    this.arrowManager.transactionBackgrounds = {};

    this.firstLevelChildTransactions = [];
  }


  destroy() {
    this.paper.clear();
    this.paper.remove();
  }


  hightlightHoverItem({id, type}){
    this.removeHoveringHighlight();

    const events = Object.values(this.arrowManager?.eventArrows) || [];
    const invoked_transactions = Object.values(this.arrowManager?.invokedTransactions) || [];
    const child_transactions = Object.values(this.arrowManager?.childTransactions) || [];
    const nodes =  Object.values(this.nodeManager?.nodes) || [];

    let hoveringItem = null;
    if(type === "event"){
      hoveringItem = events?.find( item  => item?.id === id) ;
    }else if (type === "invoked_transaction"){
      hoveringItem = invoked_transactions?.find( item  => item?.id === id) ;
    }else if (type === "nested_transaction"){
      hoveringItem = child_transactions?.find( item  => item?.id === id) ;
    }else if (type === "clusterlet" || type === "analytics" || type === "external_function"){
      hoveringItem = nodes?.find( item  => item?.id === id) ;
    }

    if(hoveringItem){
      hoveringItem?.highlightElement?.attr({ fill: this.hoveringElementConfig?.mouseOverFill || "#fcfc69" });
      this.hoveringItem = {id, type, highlightElement: hoveringItem?.highlightElement};
    }
  }

  removeHoveringHighlight = () => {
    if(this.hoveringItem){
      if(this.selectedItem?.id === this.hoveringItem?.id){
        this.hightLighSelectedElementInDiagram({id: this.hoveringItem?.id, type: this.hoveringItem?.type});
      }else{
        this.hoveringItem?.highlightElement?.attr({ fill: "#fff" });
      }
    }
    this.hoveringItem = null;
  }


  /**
   * Sets flags to control rerendering and construct updates.
   *
   * @param {Object} options
   * @param {boolean} [options.preventRerender=false] - Whether to prevent complete rerender.
   * @param {boolean} [options.preventUpdate=false] - Whether to prevent constructs update.
   */
  setRenderAndUpdateFlags({ preventRerender = false, preventUpdate = false }) {
    this.preventCompleteRerender = preventRerender;
    this.preventConstructsUpdate = preventUpdate;
  }

  scrollToElementIfNeeded(focusElement, smoothScroll = false) {
    if (!focusElement) return;
  
    const container = document.getElementById("transaction_container");
    const bottomDrawer = document.getElementById("bottom_drawer_container");
  
    if (!container || !this.arrowManager?.transactionElemnts) return;
  
    // Original container height
    let containerHeight = this.containerHeight;
  
    // Subtract bottom drawer height if present
    if (bottomDrawer) {
      const bottomDrawerHeight = bottomDrawer.clientHeight || 0;
      containerHeight = containerHeight - bottomDrawerHeight;
    }
  
    const allTransactionElements = [
      ...Object.entries(this.arrowManager?.transactionElemnts),
      ...Object.entries(this.nodeManager?.nodes)
    ];
  
    allTransactionElements.forEach(([id, el]) => {
      if (focusElement.id === id && el?.group?.getBBox) {
        const bbox = el.group.getBBox();
        const targetY = bbox.y;
        const scrollTarget = targetY - containerHeight / 2;
  
        container.scrollTo({
          top: scrollTarget,
          behavior: smoothScroll ? "smooth" : "auto"
        });
      }
    });
  }

  /**
     * Expands all parent transactions of a selected item so that it becomes visible.
     *
     * @param {Object} params
     * @param {string} params.id - The ID of the selected item.
     * @param {string} params.type - The type of the selected item (optional, in case of additional logic later).
  */
  expandParentOfSelectedItem({ id, type }) {

      const elementsMap = flattenTree(this.transactionChildrenInOrder || [])
      const expandedMap = this.arrowManager?.expandedChildTransactions;

      if (!elementsMap || !expandedMap) {
        return;
      }

      const selectedItem = elementsMap?.find(item => item?.id === id);
      if (!selectedItem) {
        return;
      }

      const rootId = this.selectedTransaction?._id;
      const alreadyExpanded = expandedMap[selectedItem?.parent];


      // If parent is root or already expanded, do nothing
      if (selectedItem?.parent === rootId || alreadyExpanded) return;

      // Get all parent IDs up to root
      const parentIds = findAllParents({
        transactionId: rootId,
        selectedItem,
        elementsList: elementsMap
      });


      // Expand all parent nodes
      parentIds.forEach(parent => {
        expandedMap[parent] = true;
      });

      // Refresh UI
      this.clearAllElements();
      this.init();
  }

  removeExistingInlineInput() {
    
    
  }


}



